import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'

import lottie from 'lottie-web/build/player/lottie_light'
import confettiAnimation from '~/assets/animations/confetti.json'
import { Styles as AnatomyS } from '~/pages/anatomy/scratch/styles'
import {
  COMMUNICATION_CARD_BACKGROUND,
  COMMUNICATION_GRADIENT_COLORS,
} from '~/pages/communication/scratch/libs/components/ScratchCommunication/constants'
import { CommunicationScratchStyles as CS } from '~/pages/communication/scratch/libs/components/ScratchCommunication/styles'
import { ScratchCard } from '~/shared/components/ScratchCard'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { eventLogger } from '~/shared/services/eventLogger'
import { selectDynamicDiscount } from '~/shared/store/common/selects'
import { selectUserEmail } from '~/shared/store/user/selects'
import { TPageProps } from '~/shared/types/commonTypes'

const DEFAULT_DISCOUNT = 56

const ScratchCommunication = ({ nextPagePath, pageId }: TPageProps) => {
  const discount = useSelector(selectDynamicDiscount)
  const email = useSelector(selectUserEmail)
  const [isScratchingFinished, setIsScratchingFinished] = useState(false)
  const [isConfettiVisible, setIsConfettiVisible] = useState(false)
  const confettiRef = useRef<HTMLDivElement>(null)
  const confettiTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const scratchStartedRef = useRef(false)
  const scratchAttemptsRef = useRef(0)

  const goToNextPage = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'scratch_card',
  })

  useEffect(() => {
    if (confettiRef.current && isConfettiVisible) {
      lottie.loadAnimation({
        container: confettiRef.current,
        animationData: confettiAnimation,
        name: 'confetti',
        autoplay: true,
        loop: false,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMin slice',
        },
      })
    }

    return () => lottie.destroy('confetti')
  }, [isConfettiVisible])

  useEffect(() => {
    if (isScratchingFinished) {
      document.body.setAttribute('data-header-dark', 'true')
    }
    return () => {
      document.body.removeAttribute('data-header-dark')
    }
  }, [isScratchingFinished])

  useEffect(() => {
    eventLogger.logScratchDiscountPageShow()
  }, [])

  useEffect(() => {
    return () => clearTimeout(confettiTimerRef.current)
  }, [])

  const handleStart = useCallback(() => {
    scratchAttemptsRef.current += 1
    if (!scratchStartedRef.current) {
      scratchStartedRef.current = true
      eventLogger.logScratchingStarted()
    }
  }, [])

  const handleComplete = useCallback(() => {
    setIsScratchingFinished(true)
    eventLogger.logScratchingFinished(scratchAttemptsRef.current)
    confettiTimerRef.current = setTimeout(() => setIsConfettiVisible(true), 500)
  }, [])

  const handleContinue = () => goToNextPage('')

  return (
    <AnatomyS.Wrapper>
      <AnatomyS.DarkOverlay $visible={isScratchingFinished} />

      <AnatomyS.StyledContainer>
        <AnatomyS.Title>
          <Trans i18nKey="onboarding.scratchAnatomy.title" />
        </AnatomyS.Title>

        <AnatomyS.Subtitle>
          <Trans i18nKey="onboarding.scratchAnatomy.subtitle" />
        </AnatomyS.Subtitle>

        <AnatomyS.ScratchSection $finished={isScratchingFinished}>
          <ScratchCard
            discount={discount?.amount ?? DEFAULT_DISCOUNT}
            email={email}
            isDark={isScratchingFinished}
            gradientColors={COMMUNICATION_GRADIENT_COLORS}
            cardBackground={COMMUNICATION_CARD_BACKGROUND}
            bottomOverlayColor="rgba(0,0,0,0.15)"
            canvasOverlayOpacity={0.15}
            hintAnimationFilter="hue-rotate(-34deg) brightness(0.9)"
            promoCheckmark={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="10"
                viewBox="0 0 13 10"
                fill="none"
              >
                <path
                  d="M0.891602 4.45842L4.45842 8.02524L11.5921 0.891602"
                  stroke="#FA9415"
                  strokeWidth="1.78341"
                  strokeLinecap="round"
                />
              </svg>
            }
            onStart={handleStart}
            onComplete={handleComplete}
          />
          {isConfettiVisible && (
            <AnatomyS.AppliedText>
              <Trans i18nKey="onboarding.scratchAnatomy.appliedAutomatically" />
            </AnatomyS.AppliedText>
          )}
        </AnatomyS.ScratchSection>

        <AnatomyS.BottomSection>
          {isConfettiVisible && (
            <CS.ContinueButton type="button" onClick={handleContinue}>
              <Trans i18nKey="actions.continue" />
            </CS.ContinueButton>
          )}
        </AnatomyS.BottomSection>
      </AnatomyS.StyledContainer>

      {isConfettiVisible &&
        createPortal(
          <AnatomyS.ConfettiAnimation ref={confettiRef} />,
          document.body,
        )}
    </AnatomyS.Wrapper>
  )
}

export default ScratchCommunication
